/* GrowthOS — P&C analytics dashboard (Quotes / KPIs / Workbook).
   Reskin of the legacy `quoteAi` Bootstrap dashboard. This page is a STATIC
   mock (no backend) — the KPI ratios, transaction counts and workbook rows are
   placeholder values carried over verbatim from the legacy page; only the
   presentation was converted to the GrowthOS theme. No dedicated design file
   exists for it, so the layout is extended from the standard dashboard
   (homeCRM.jsx) patterns by inference: `.gos-page-head`, `.gos-kpi`, `.gos-card`,
   `.gos-tbl`, `.gos-badge`, `.gos-seg` + a small scoped `.gos-pnc-*` block.
   The "Create New Quote" popup reuses the shared `QuoteCreateModal`. The legacy
   floating right icon-rail (decorative tooltip images) was dropped. */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import QuoteCreateModal from './QuoteCreateModal';

const PATHS = {
  kpi: 'M22 12h-4l-3 9L9 3l-3 9H2',
  export: 'M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2M12 3v12M8 7l4-4 4 4',
  quote: 'M14 3v5h5M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-5ZM8 13h8M8 17h5',
  plus: 'M12 5v14M5 12h14',
  chevron: 'M6 9l6 6 6-6',
  calendar: 'M8 2v4M16 2v4M3 9h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z',
  search: 'M21 21l-4.3-4.3M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z',
  book: 'M4 19.5A2.5 2.5 0 0 1 6.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z',
  dots: 'M12 5h.01M12 12h.01M12 19h.01',
  tx: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  down: 'M12 5v14M5 12l7 7 7-7',
  up: 'M12 19V5M5 12l7-7 7 7',
  alert: 'M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0',
};

// legacy floating right icon-rail (Workbook / Alfred Alerts) — decorative,
// no backend action; kept for parity with the legacy page.
const RAIL = [
  { key: 'workbook', glyph: 'book', label: 'Workbook' },
  { key: 'alfred', glyph: 'alert', label: 'Alfred Alerts' },
];

const Ic = ({ name, size = 16, className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d={PATHS[name]} />
  </svg>
);

// ---- static (mock) data — carried verbatim from the legacy page ----
const KPIS = [
  { label: 'Quotes / Indications', tone: 'rose' },
  { label: 'Bound / Indications', tone: 'blue' },
  { label: 'Bound / Quote', tone: 'green' },
  { label: 'Issued / Bound', tone: 'amber' },
  { label: 'Straight-thru / Quotes', tone: 'purple' },
  { label: 'Renewed / Renewable', tone: 'blue' },
  { label: 'Issued / Indications', tone: 'green' },
  { label: 'Issued / Quotes', tone: 'rose' },
];
const KPI_PCT = 66;
const KPI_NUM = '755';
const KPI_DEN = 'of 1,000';

const TX = [
  { name: 'Indications', total: 150, tags: [['120', 'Completed', 'green'], ['30', 'Incomplete', 'red']], trend: '80%' },
  { name: 'Quotes', total: 120, tags: [['80', 'Completed', 'green'], ['25', 'Pending', 'amber'], ['30', 'Incomplete', 'red']], trend: '62.5%' },
  { name: 'Bound', total: 75, tags: [['60', 'Completed', 'green'], ['10', 'Pending', 'amber'], ['5', 'Incomplete', 'red']], trend: '80%' },
  { name: 'Issued', total: 63, tags: [['55', 'Completed', 'green'], ['5', 'Pending', 'amber'], ['3', 'Incomplete', 'red']], trend: '80%' },
  { name: 'Renewals', total: 27, tags: [['20', 'Completed', 'green'], ['5', 'Pending', 'amber'], ['2', 'Incomplete', 'red']], trend: null },
];

const TX_LEGEND = [
  ['green', 'Completed'],
  ['amber', 'Pending'],
  ['red', 'Incompleted'],
  ['blue', 'Reinstatements'],
];

const WORKBOOK = Array.from({ length: 6 }).map(() => ({
  ref: 'Q000001245',
  insured: 'AL Church Methodist',
  agent: 'John Doe',
  agency: 'Doe Insurance',
  lob: 'WC',
  effective: '03/23/2024',
}));

// small progress ring for the KPI ratio cards
const PncRing = ({ value, color, size = 56 }) => {
  const sw = 6;
  const r = (size - sw) / 2;
  const c = size / 2;
  const C = 2 * Math.PI * r;
  const dash = (value / 100) * C;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      <circle cx={c} cy={c} r={r} fill="none" strokeWidth={sw} style={{ stroke: 'var(--gos-surface-3)' }} />
      <circle
        cx={c}
        cy={c}
        r={r}
        fill="none"
        strokeWidth={sw}
        strokeLinecap="round"
        strokeDasharray={`${dash} ${C - dash}`}
        transform={`rotate(-90 ${c} ${c})`}
        style={{ stroke: color }}
      />
      <text x={c} y={c + 4} textAnchor="middle" fontSize="14" fontWeight="700" style={{ fill: 'var(--gos-text)' }}>
        {value}%
      </text>
    </svg>
  );
};

// dashboard view switcher — mirrors homeCRM's GosDashboardSwitch, selected 'pnc'
const PncDashboardSwitch = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const views = [
    { id: 'standard', label: 'Standard Dashboard', sub: 'Pipeline, tickets & opportunities', to: '/dashboard' },
    { id: 'pnc', label: 'P&C Dashboard', sub: 'Quotes, KPIs & workbook', to: '/pncQuote/dashboard' },
  ];
  const sel = 'pnc';
  const cur = views.find((v) => v.id === sel);
  return (
    <div className={'gos-dash-switch' + (open ? ' open' : '')}>
      <button type="button" className="gos-btn gos-btn-ghost" onClick={() => setOpen((o) => !o)}>
        <span className="gos-ds-dot"></span>
        {cur.label}
        <Ic name="chevron" size={14} className="gos-caret" />
      </button>
      {open && (
        <div className="gos-dash-menu">
          {views.map((v) => (
            <button
              key={v.id}
              type="button"
              className={'gos-dash-menu-item' + (v.id === sel ? ' sel' : '')}
              onClick={() => {
                setOpen(false);
                if (v.id !== sel) navigate(v.to);
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div>{v.label}</div>
                <div className="gos-di-sub">{v.sub}</div>
              </div>
              {v.id === sel && <Ic name="chevron" size={16} className="gos-di-check" style={{ transform: 'none' }} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const QuoteAiDashboard = () => {
  const [show, setShow] = useState(false);
  const [period, setPeriod] = useState('Monthly');
  const [startDate, setStartDate] = useState('2024-11-01');
  const [endDate, setEndDate] = useState('2024-11-30');
  const [pageSize, setPageSize] = useState('5');
  const [search, setSearch] = useState('');

  return (
    <div className="inner-page gos-pnc">
      <div className="gos-page-head">
        <div>
          <div className="gos-page-title">P&amp;C Dashboard</div>
          <div className="gos-page-sub">Quotes, KPIs &amp; workbook overview</div>
        </div>
        <div className="gos-page-actions">
          <PncDashboardSwitch />
          <button type="button" className="gos-btn gos-btn-ghost">
            <Ic name="export" size={16} />
            Export
          </button>
          <button type="button" className="gos-btn gos-btn-primary" onClick={() => setShow(true)}>
            <Ic name="plus" size={16} />
            New Quote
          </button>
        </div>
      </div>

      {/* period / date-range toolbar */}
      <div className="gos-pnc-section">
        <div className="gos-pnc-period">
          <div className="gos-seg">
            {['Monthly', 'Quarterly', 'Yearly'].map((p) => (
              <button
                key={p}
                type="button"
                className={'gos-seg-btn' + (period === p ? ' on' : '')}
                onClick={() => setPeriod(p)}
              >
                {p}
              </button>
            ))}
          </div>
          <div className="gos-pnc-daterange">
            <label className="gos-pnc-date">
              <Ic name="calendar" size={16} />
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </label>
            <span className="gos-pnc-date-sep">to</span>
            <label className="gos-pnc-date">
              <Ic name="calendar" size={16} />
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </label>
          </div>
          <button type="button" className="gos-btn gos-btn-primary">Submit</button>
        </div>
      </div>

      {/* KPIs */}
      <div className="gos-pnc-section">
        <div className="gos-pnc-kpihead">
          <div className="gos-card-title">
            <Ic name="kpi" size={17} />
            Key Performance Indicators
          </div>
          <span className="gos-pnc-kpihead-sub">November, 2024</span>
        </div>
        <div className="gos-pnc-kpis">
          {KPIS.map((k, i) => (
            <div className="gos-kpi gos-pnc-kpi" key={i}>
              <div className="gos-pnc-kpi-top">
                <div className="gos-kpi-label">{k.label}</div>
                <span className="gos-kpi-delta gos-delta-up">
                  <Ic name="up" size={13} />
                  12.5%
                </span>
              </div>
              <div className="gos-pnc-kpi-body">
                <PncRing value={KPI_PCT} color={`var(--gos-${k.tone}-fg)`} />
                <div>
                  <div className="gos-pnc-kpi-num">{KPI_NUM}</div>
                  <div className="gos-pnc-kpi-den">{KPI_DEN}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* transactions + workbook */}
      <div className="gos-pnc-section gos-pnc-2col">
        {/* transaction status */}
        <div className="gos-card">
          <div className="gos-card-head">
            <div className="gos-card-title">
              <Ic name="tx" size={17} />
              Transactions Status Details
            </div>
          </div>
          <div className="gos-card-pad">
            {TX.map((t, i) => (
              <div className="gos-pnc-tx-row" key={i}>
                <div className="gos-pnc-tx-main">
                  <div className="gos-pnc-tx-name">
                    {t.name}
                    <span className="gos-pnc-tx-total">{t.total}</span>
                  </div>
                  <div className="gos-pnc-tx-tags">
                    {t.tags.map(([n, label, tone], j) => (
                      <span className={'gos-pnc-tx-tag ' + tone} key={j}>
                        <b>{n}</b> {label}
                      </span>
                    ))}
                  </div>
                </div>
                {t.trend && (
                  <div className="gos-pnc-tx-trend">
                    <Ic name="down" size={13} />
                    {t.trend}
                  </div>
                )}
              </div>
            ))}
            <div className="gos-pnc-tx-legend">
              {TX_LEGEND.map(([tone, label], i) => (
                <span className="gos-pnc-tx-leg" key={i}>
                  <span className={'gos-pnc-tx-leg-dot ' + tone} />
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* workbook */}
        <div className="gos-card gos-pnc-wb">
          <div className="gos-card-head gos-pnc-wb-head">
            <div className="gos-card-title">
              <Ic name="book" size={17} />
              Workbook
            </div>
            <div className="gos-pnc-wb-actions">
              <div className="gos-pnc-wb-search">
                <Ic name="search" size={15} />
                <input
                  type="text"
                  placeholder="Search here..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <button type="button" className="gos-btn gos-btn-ghost">
                <Ic name="book" size={15} />
                Full Workbook
              </button>
              <button type="button" className="gos-btn gos-btn-ghost" onClick={() => setShow(true)}>
                <Ic name="plus" size={15} />
                Quotes
              </button>
              <select className="gos-pnc-wb-size" value={pageSize} onChange={(e) => setPageSize(e.target.value)}>
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
              </select>
            </div>
          </div>
          <div className="gos-pnc-wb-table">
            <table className="gos-tbl">
              <thead>
                <tr>
                  <th style={{ width: 34 }}></th>
                  <th>Ref</th>
                  <th>Insured Name</th>
                  <th>Agent Name</th>
                  <th>Agency Name</th>
                  <th>LOB</th>
                  <th>Effective Date</th>
                  <th>Alfred Alerts</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {WORKBOOK.map((r, i) => (
                  <tr key={i}>
                    <td>
                      <span className="gos-pnc-wb-exp"><Ic name="plus" size={13} /></span>
                    </td>
                    <td><span className="gos-cell-name">{r.ref}</span></td>
                    <td>{r.insured}</td>
                    <td className="gos-muted">{r.agent}</td>
                    <td className="gos-muted">{r.agency}</td>
                    <td><span className="gos-pnc-lob">{r.lob}</span></td>
                    <td className="gos-muted">{r.effective}</td>
                    <td></td>
                    <td>
                      <button type="button" className="gos-pnc-wb-more"><Ic name="dots" size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="gos-pnc-pager">
            <button type="button" className="gos-pnc-page" disabled>Previous</button>
            <button type="button" className="gos-pnc-page on">1</button>
            <button type="button" className="gos-pnc-page">2</button>
            <button type="button" className="gos-pnc-page">3</button>
            <button type="button" className="gos-pnc-page">Next</button>
          </div>
        </div>
      </div>

      {/* floating right icon-rail */}
      <div className="gos-pnc-rail">
        {RAIL.map((r) => (
          <button key={r.key} type="button" className="gos-pnc-rail-btn" title={r.label} aria-label={r.label}>
            <Ic name={r.glyph} size={20} />
          </button>
        ))}
      </div>

      {show && <QuoteCreateModal onClose={() => setShow(false)} />}
    </div>
  );
};

export default QuoteAiDashboard;
